import { cicFeeReasonsApiV2 } from 'app/services/api';
import _ from 'lodash';

import { CicReasonAppliesTo } from 'constants/cicReason';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const CIC_LOAN_TERM_REASONS_ACTION_TYPE = createNamedActions('cicLoanTermReasons', {
  GET_REASONS: 'GET_REASONS',
  CREATE_REASON: 'CREATE_REASON',
  EDIT_REASON: 'EDIT_REASON',
  DELETE_REASON: 'DELETE_REASON',
  REORDER_REASONS: 'REORDER_REASONS',
});

// A list response is applied only if no newer load or reorder started after it, so a slow reload
// can never overwrite the order of a later drag.
const latestListChange = { id: 0 };

const startListChange = () => {
  latestListChange.id += 1;

  return latestListChange.id;
};

export const getCicLoanTermReasonsAction = catchAsyncActionError(() => async dispatch => {
  const changeId = startListChange();
  const { data } = await cicFeeReasonsApiV2.getCicLoanTermReasons();

  if (changeId !== latestListChange.id) {
    return null;
  }

  return dispatch({
    type: CIC_LOAN_TERM_REASONS_ACTION_TYPE.GET_REASONS,
    payload: data,
  });
});

export const getActiveCicLoanTermReasonsAction = catchAsyncActionError(() => async () => {
  const { data } = await cicFeeReasonsApiV2.getCicLoanTermReasons({ activeOnly: true });

  return data ?? [];
});

export const createCicLoanTermReasonAction = catchAsyncActionError(
  ({ name, description, isActive, commentTemplates = [] }) =>
    async dispatch => {
      const { data } = await cicFeeReasonsApiV2.createCicFeeReasons({
        name,
        description,
        isActive,
        commentTemplates,
        appliesTo: CicReasonAppliesTo.LOAN_TERM,
      });

      return dispatch({
        type: CIC_LOAN_TERM_REASONS_ACTION_TYPE.CREATE_REASON,
        payload: data,
      });
    },
);

export const editCicLoanTermReasonAction = catchAsyncActionError(
  ({ cicFeeReasonId, name, description, isActive, commentTemplates }) =>
    async dispatch => {
      const { data } = await cicFeeReasonsApiV2.editCicFeeReasons({
        cicFeeReasonId,
        name,
        description,
        isActive,
        appliesTo: CicReasonAppliesTo.LOAN_TERM,
        // An omitted list keeps the stored templates; only the reason drawer edits them.
        ...getConditionalObject(!_.isUndefined(commentTemplates), { commentTemplates }),
      });

      return dispatch({
        type: CIC_LOAN_TERM_REASONS_ACTION_TYPE.EDIT_REASON,
        payload: data,
      });
    },
);

export const deleteCicLoanTermReasonAction = catchAsyncActionError(
  ({ cicFeeReasonId }) =>
    async dispatch => {
      await cicFeeReasonsApiV2.deleteCicFeeReasons({ cicFeeReasonId });

      return dispatch({
        type: CIC_LOAN_TERM_REASONS_ACTION_TYPE.DELETE_REASON,
        payload: cicFeeReasonId,
      });
    },
);

export const reorderCicLoanTermReasonsAction = catchAsyncActionError(
  ({ orderedIds, ownReasonIds }) =>
    async dispatch => {
      startListChange();
      dispatch({
        type: CIC_LOAN_TERM_REASONS_ACTION_TYPE.REORDER_REASONS,
        payload: orderedIds,
      });

      return cicFeeReasonsApiV2.reorderCicFeeReasons({ cicFeeReasonIds: ownReasonIds });
    },
);
